import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { ThumbsUp, ThumbsDown, X } from "lucide-react";

const FeedbackComponent = ({ messageId, onFeedbackSubmit }) => {
  const [feedbackType, setFeedbackType] = useState(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [category, setCategory] = useState("");
  const [severity, setSeverity] = useState("Low");
  const [comment, setComment] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);

  const negativeCategories = [
    "wrong_business_context",
    "ambiguous_suggestion",
    "incorrect_mapping",
    "missing_reference",
    "wrong_source_table",
    "others",
  ];

  // Prevent body scroll and interactions when modal is open
  useEffect(() => {
    if (showFeedbackModal) {
      // Prevent scrolling
      document.body.style.overflow = 'hidden';
      
      // Add a class to body to indicate modal is open
      document.body.classList.add('modal-open');
      
      // Prevent all interactions with background
      const backdrop = document.querySelector('.modal-backdrop');
      if (backdrop) {
        backdrop.style.pointerEvents = 'auto';
      }
    } else {
      document.body.style.overflow = 'unset';
      document.body.classList.remove('modal-open');
    }
    
    return () => {
      document.body.style.overflow = 'unset';
      document.body.classList.remove('modal-open');
    };
  }, [showFeedbackModal]);

  const formatCategoryForDisplay = (category) => {
    return category
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const handleThumbClick = (type) => {
    if (isSubmitted && feedbackType === type) {
      setIsSubmitted(false);
      setFeedbackType(null);
      return;
    }
    
    if (!isSubmitted) {
      if (type === "positive") {
        // Submit positive feedback immediately without modal
        const feedbackData = {
          type: "positive",
          comment: "",
          timestamp: new Date().toISOString()
        };

        if (onFeedbackSubmit) {
          onFeedbackSubmit(messageId, feedbackData);
        }

        setFeedbackType("positive");
        setIsSubmitted(true);
      } else {
        // Show modal for negative feedback
        setFeedbackType(type);
        setShowFeedbackModal(true);
        setCategory("");
        setSeverity("Low");
        setComment("");
      }
    }
  };

  const handleSubmitFeedback = () => {
    if (feedbackType === "negative" && !category) {
      alert("Please select a category");
      return;
    }

    if (feedbackType === "negative" && category === "others" && !comment.trim()) {
      alert("Please provide a comment for 'others' category");
      return;
    }

    const normalizedComment = comment.trim().replaceAll(/\s+/g, ' ');

    const feedbackData = {
      type: feedbackType,
      category: category,
      severity: severity,
      comment: normalizedComment,
      timestamp: new Date().toISOString()
    };

    if (onFeedbackSubmit) {
      onFeedbackSubmit(messageId, feedbackData);
    }

    setIsSubmitted(true);
    setShowFeedbackModal(false);
  };

  const handleCancel = () => {
    setShowFeedbackModal(false);
    setCategory("");
    setSeverity("Low");
    setComment("");
    setFeedbackType(null);
  };

  const handleOverlayClick = (e) => {
    // Only close if clicking directly on the overlay, not the modal content
    if (e.target === e.currentTarget) {
      handleCancel();
    }
  };

  return (
    <>
      <div className="flex items-center gap-1">
        {(!isSubmitted || feedbackType === "positive") && (
          <button
            onClick={() => handleThumbClick("positive")}
            className={`group relative p-1 rounded-md transition-all ${
              feedbackType === "positive" && isSubmitted
                ? "bg-green-100 text-green-600"
                : "text-gray-400 hover:text-green-500 hover:bg-green-50"
            } cursor-pointer`}
            title={isSubmitted && feedbackType === "positive" ? "Click to unselect" : "Good response"}
            disabled={showFeedbackModal}
          >
            <ThumbsUp className="w-4 h-4" />
          </button>
        )}

        {(!isSubmitted || feedbackType === "negative") && (
          <button
            onClick={() => handleThumbClick("negative")}
            className={`group relative p-1 rounded-md transition-all ${
              feedbackType === "negative" && isSubmitted
                ? "bg-red-100 text-red-600"
                : "text-gray-400 hover:text-red-500 hover:bg-red-50"
            } cursor-pointer`}
            title={isSubmitted && feedbackType === "negative" ? "Click to unselect" : "Bad response"}
            disabled={showFeedbackModal}
          >
            <ThumbsDown className="w-4 h-4" />
          </button>
        )}

        {isSubmitted && (
          <span className="text-xs text-green-600 ml-1 flex items-center gap-1">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            Thanks!
          </span>
        )}
      </div>

      {showFeedbackModal && (
        <button
          type="button"
          className="modal-backdrop fixed inset-0 flex items-center justify-center p-4 border-0"
          onClick={handleOverlayClick}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              handleCancel();
            }
          }}
          aria-label="Close feedback modal"
          style={{ 
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
            pointerEvents: 'auto',
            cursor: 'default',
            border: 'none',
            padding: 0,
            background: 'rgba(0, 0, 0, 0.5)'
          }}
        >
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
          <section 
            className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto relative"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.stopPropagation();
              }
            }}
            aria-label="Feedback form"
            style={{ 
              zIndex: 10000,
              pointerEvents: 'auto'
            }}
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  Provide Detailed Feedback
                </h2>
                <button
                  onClick={handleCancel}
                  className="text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-gray-100 transition-colors"
                  type="button"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label id="feedback-category-label" htmlFor="feedback-category" className="block text-sm font-medium text-gray-700 mb-2">
                    Tell us more:
                  </label>
                  <fieldset id="feedback-category" className="grid grid-cols-2 gap-2" aria-labelledby="feedback-category-label">
                    {negativeCategories.map((cat) => (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => setCategory(cat)}
                        className={`px-3 py-2 text-sm rounded-md border transition-colors ${
                          category === cat
                            ? "bg-blue-50 border-blue-500 text-blue-700"
                            : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        {formatCategoryForDisplay(cat)}
                      </button>
                    ))}
                  </fieldset>
                </div>

                <div>
                  <label id="feedback-severity-label" htmlFor="feedback-severity" className="block text-sm font-medium text-gray-700 mb-2">
                    Severity
                  </label>
                  <fieldset id="feedback-severity" className="flex gap-2" aria-labelledby="feedback-severity-label">
                    {["Low", "Medium", "High"].map((level) => (
                      <button
                        key={level}
                        type="button"
                        onClick={() => setSeverity(level)}
                        className={`flex-1 px-3 py-2 text-sm rounded-md border transition-colors ${
                          severity === level
                            ? "bg-blue-50 border-blue-500 text-blue-700"
                            : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        {level}
                      </button>
                    ))}
                  </fieldset>
                </div>

                <div>
                  <label htmlFor="feedback-comments" className="block text-sm font-medium text-gray-700 mb-2">
                    Comments {category === "others" && <span className="text-red-600">*</span>}
                  </label>
                  <textarea
                    id="feedback-comments"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder={
                      category === "others"
                        ? "Please describe the issue..."
                        : "What could be improved?"
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    rows="4"
                    maxLength={500}
                  />
                  <div className="text-xs text-gray-500 text-right mt-1">
                    {comment.length}/500 characters
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmitFeedback}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
                >
                  Submit
                </button>
              </div>
            </div>
          </section>
        </button>
      )}
    </>
  );
};

FeedbackComponent.propTypes = {
  messageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onFeedbackSubmit: PropTypes.func.isRequired,
};

export default FeedbackComponent;